import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePosts } from '../hooks/usePosts';
import { useUserProfile, useUserFollow } from '../hooks/useUser';
import { useJupiterApi } from '../hooks/useJupiterApi';
import { UserBadge } from '../components/UI/UserBadge';
import { UserAvatar } from '../components/UI/UserAvatar';
import { FollowButton } from '../components/UI/FollowButton';
import { TokenInfoCard } from '../components/UI/TokenInfoCard';
import { SpaceExplorers } from '../components/UI/SpaceExplorers';
import { RecentActivity } from '../components/UI/RecentActivity';
import { UserProfileModal } from '../components/UI/UserProfileModal';
import PostCard from '../components/Post/PostCard';
import { User, LEVELS, TokenInfo } from '../types';

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser, setUser } = useAuth();
  const navigate = useNavigate();
  
  // État pour l'utilisateur du profil (peut être différent de l'utilisateur connecté)
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Détermine si c'est le profil de l'utilisateur connecté ou d'un autre utilisateur
  const isOwnProfile = !userId || (currentUser && userId === currentUser.id);
  const user = isOwnProfile ? currentUser : profileUser;
  
  const { posts: userPosts } = usePosts(undefined, isOwnProfile ? user?.id : profileUser?.id);
  const { updateProfile, uploadProfilePicture, uploadBannerImage, getUserById, loading: profileLoading } = useUserProfile();
  const { getFollowers, getFollowing } = useUserFollow();
  const [activeTab, setActiveTab] = useState<'posts' | 'comments' | 'likes' | 'badges' | 'following' | 'followers'>('posts');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isEditing, setIsEditing] = useState(false);
  const [followers, setFollowers] = useState<User[]>([]);
  const [following, setFollowing] = useState<User[]>([]);
  const profileFileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);
  const [editForm, setEditForm] = useState({
    displayName: user?.displayName || '',
    bio: user?.bio || '',
    socialLinks: {
      twitter: user?.socialLinks?.twitter || '',
      telegram: user?.socialLinks?.telegram || '',
      website: user?.socialLinks?.website || '',
      discord: user?.socialLinks?.discord || ''
    }
  });

  const [notification, setNotification] = useState<string | null>(null);
  const [trendingTokens, setTrendingTokens] = useState<TokenInfo[]>([]);
  
  // État pour le modal de profil utilisateur (sidebar)
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Hooks pour la sidebar
  const { getTrendingTokens } = useJupiterApi();

  // Catégories pour le filtrage des posts
  const categories = [
    { value: 'all', label: 'Tous', emoji: '🌟' },
    { value: 'annonces', label: 'Annonces', emoji: '📢' },
    { value: 'feedback', label: 'Feedback', emoji: '💬' },
    { value: 'dao', label: 'DAO', emoji: '🏛️' },
    { value: 'research', label: 'Research', emoji: '🔬' },
    { value: 'meme', label: 'Meme', emoji: '😂' },
    { value: 'launchpad', label: 'Launchpad', emoji: '🚀' }
  ];

  // Filtrer les posts par catégorie
  const filteredPosts = selectedCategory === 'all' 
    ? userPosts 
    : userPosts.filter(post => post.category === selectedCategory);

  // Charger les données de l'utilisateur si ce n'est pas le profil personnel
  useEffect(() => {
    const loadProfileData = async () => {
      if (!isOwnProfile && userId) {
        setLoading(true);
        try {
          const userData = await getUserById(userId);
          setProfileUser(userData);
        } catch (error) {
          console.error('Erreur lors du chargement du profil:', error);
          setProfileUser(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [userId, isOwnProfile, getUserById]);

  // Charger les données pour la sidebar
  useEffect(() => {
    const loadSidebarData = async () => {
      try {
        const trending = await getTrendingTokens();
        setTrendingTokens(trending.slice(0, 3)); // Top 3 pour le sidebar
      } catch (error) {
        console.error('Erreur chargement données sidebar:', error);
      }
    };
    loadSidebarData();
  }, [getTrendingTokens]);

  const loadFollowData = useCallback(async () => {
    const currentUserId = isOwnProfile ? user?.id : profileUser?.id;
    if (!currentUserId) return;
    const [followersData, followingData] = await Promise.all([
      getFollowers(currentUserId),
      getFollowing(currentUserId)
    ]);
    setFollowers(followersData);
    setFollowing(followingData);
  }, [isOwnProfile, user?.id, profileUser?.id, getFollowers, getFollowing]);

  useEffect(() => {
    const currentUserId = isOwnProfile ? user?.id : profileUser?.id;
    if (currentUserId) {
      loadFollowData();
    }
  }, [isOwnProfile, user?.id, profileUser?.id, loadFollowData]);

  // Affiche la notification pendant 2s
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (user) {
      setEditForm({
        displayName: user.displayName || '',
        bio: user.bio || '',
        socialLinks: {
          twitter: user.socialLinks?.twitter || '',
          telegram: user.socialLinks?.telegram || '',
          website: user.socialLinks?.website || '',
          discord: user.socialLinks?.discord || ''
        }
      });
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const updatedUser = await updateProfile(user.id, {
      displayName: editForm.displayName,
      bio: editForm.bio,
      socialLinks: editForm.socialLinks
    });

    if (updatedUser) {
      setUser(updatedUser);
      setIsEditing(false);
    }
  };

  const handleProfilePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;
    const file = e.target.files[0];
    const imageUrl = await uploadProfilePicture(user.id, file);
    if (imageUrl && user) {
      setUser({ ...user, profilePicture: imageUrl });
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !user) return;
    const file = e.target.files[0];
    const imageUrl = await uploadBannerImage(user.id, file);
    if (imageUrl && user) {
      setUser({ ...user, bannerImage: imageUrl });
    }
  };

  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  const handleUserAvatarClick = (userId: string) => {
    setSelectedUserId(userId);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedUserId('');
  };

  const handleViewFullProfile = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-500">Chargement du profil...</div>
      </div>
    );
  }

  if (!isOwnProfile && !profileUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500">Profil non trouvé</div>
      </div>
    );
  }

  if (!user && isOwnProfile) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Connectez votre wallet</h2>
          <p className="text-gray-400">Vous devez être connecté pour voir votre profil</p>
        </div>
      </div>
    );
  }

  // Utilise profileUser pour un autre utilisateur, sinon utilise user
  const displayUser = isOwnProfile ? user : profileUser;
  
  if (!displayUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-500">Utilisateur non trouvé</div>
      </div>
    );
  }

  const getUserLevel = () => {
    if (!displayUser?.xp) return LEVELS[0];
    return LEVELS.find(level => displayUser.xp >= level.minXP) || LEVELS[0];
  };

  const getNextLevel = () => {
    if (!displayUser?.xp) return LEVELS[1];
    const currentIndex = LEVELS.findIndex(level => displayUser.xp >= level.minXP);
    // Si déjà au dernier niveau, retourne null
    if (currentIndex === LEVELS.length - 1) return null;
    return LEVELS[currentIndex + 1] || null;
  };

  const getProgressToNextLevel = () => {
    if (!displayUser?.xp) return 0;
    const currentLevel = getUserLevel();
    const nextLevel = getNextLevel();
    // Si au niveau max, barre à 100%
    if (!nextLevel) return 100;
    const currentLevelXP = currentLevel.minXP;
    const nextLevelXP = nextLevel.minXP;
    const progressXP = displayUser.xp - currentLevelXP;
    const requiredXP = nextLevelXP - currentLevelXP;
    return Math.min(100, Math.max(0, (progressXP / requiredXP) * 100));
  };

  const currentLevel = getUserLevel();
  const nextLevel = getNextLevel();
  const progressPercent = getProgressToNextLevel();

  return (
    <div className="min-h-screen">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 max-w-7xl mx-auto px-4">
        {/* Main Profile Content */}
        <div className="flex-1 lg:max-w-2xl space-y-4 sm:space-y-6">
          {/* Banner Section - Style X/Twitter Simplifié */}
          <div className="cosmic-card p-0 overflow-hidden">
        <div className="relative h-48 sm:h-56 bg-gradient-to-br from-emerald-800/40 via-cyan-800/40 to-purple-800/40">
          {displayUser?.bannerImage && (
            <img 
              src={displayUser.bannerImage} 
              alt="Banner" 
              className="w-full h-full object-cover"
            />
          )}
        </div>
        
        {/* Profile Info - Mobile First Layout */}
        <div className="p-4 sm:p-6 md:p-8">
          <div className="space-y-4 sm:space-y-6">
            {/* Mobile: Stack Avatar and Info Vertically, Desktop: Side by Side */}
            <div className="flex flex-col items-center sm:items-start space-y-4 sm:space-y-0 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-col items-center sm:items-start sm:flex-row sm:space-x-4 md:space-x-6">
                {/* Avatar - Responsive Size */}
                <div className="relative -mt-12 sm:-mt-16 md:-mt-20 lg:-mt-24">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-gray-900 shadow-2xl bg-gray-800/40 flex items-center justify-center">
                    {displayUser?.profilePicture ? (
                      <img
                        src={displayUser.profilePicture}
                        alt={displayUser.displayName || 'Photo de profil'}
                        className="w-full h-full object-cover object-center"
                      />
                    ) : (
                      <img
                        src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${displayUser?.username || displayUser?.id}`}
                        alt="Avatar par défaut"
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                </div>
                
                {/* User Info - Mobile Centered, Desktop Left Aligned */}
                <div className="text-center sm:text-left space-y-1 sm:space-y-2 lg:space-y-3 flex-1">
                  <div className="flex flex-col sm:flex-row items-center sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                    <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-emerald-100 truncate max-w-full leading-tight">
                      {displayUser?.displayName || 'Space Explorer'}
                    </h1>
                    {displayUser?.isVerified && (
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-6 md:h-6 text-cyan-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <p className="text-emerald-300/80 text-sm sm:text-base font-medium">@{displayUser?.username || 'jupiter_user'}</p>
                  {displayUser?.bio && (
                    <p className="text-emerald-200 text-sm sm:text-base md:text-base leading-relaxed max-w-full sm:max-w-md lg:max-w-lg px-2 sm:px-0">
                      {displayUser.bio}
                    </p>
                  )}
                  
                  {/* Level & XP - Mobile Responsive */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 md:space-x-6 pt-1 sm:pt-2 lg:pt-3">
                    <div className="flex items-center space-x-2 bg-gray-800/50 px-2 lg:px-3 py-1 rounded-full border border-emerald-800/30">
                      <span className="text-base lg:text-lg">{getUserLevel().badge}</span>
                      <span className="text-emerald-300/80 text-xs sm:text-sm font-medium">
                        Niveau {getUserLevel().level} • {getUserLevel().title}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 bg-gray-800/50 px-2 lg:px-3 py-1 rounded-full border border-emerald-800/30">
                      <span className="text-emerald-300/80 text-xs sm:text-sm font-medium">XP:</span>
                      <span className="text-cyan-300 font-bold neon-text text-sm sm:text-base">{displayUser?.xp || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Button - Style X/Twitter Simplifié */}
              <div className="mt-3 sm:mt-0">
                {isOwnProfile ? (
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-6 py-2 border-2 border-emerald-700 text-emerald-100 font-semibold rounded-full hover:bg-emerald-800/30 transition-all duration-200 text-sm"
                  >
                    {isEditing ? 'Annuler' : 'Modifier le profil'}
                  </button>
                ) : (
                  <FollowButton 
                    targetUserId={displayUser.id} 
                    onFollowChange={() => loadFollowData()} 
                  />
                )}
              </div>
            </div>
            {/* Stats - Style X/Twitter Simplifié */}
            <div className="flex flex-wrap gap-4 pt-3 text-sm">
              <div>
                <span className="font-bold text-white">{following.length}</span>
                <span className="text-gray-400 ml-1">Abonnements</span>
              </div>
              
              <div>
                <span className="font-bold text-white">{followers.length}</span>
                <span className="text-gray-400 ml-1">Abonnés</span>
              </div>
              
              <div>
                <span className="font-bold text-cyan-300">{displayUser?.xp || 0}</span>
                <span className="text-gray-400 ml-1">XP</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Form Modal - Mobile Responsive */}
      {isEditing && isOwnProfile && (
        <div className="cosmic-card p-3 sm:p-4 md:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-emerald-100 mb-3 sm:mb-4">Modifier le profil</h2>
          <form onSubmit={handleSaveProfile} className="space-y-3 sm:space-y-4">
            {/* Photo de profil */}
            <div>
              <label className="block text-emerald-200 text-sm font-medium mb-2">Photo de profil</label>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-emerald-700/50 bg-gray-800/40 flex items-center justify-center">
                  {displayUser?.profilePicture ? (
                    <img
                      src={displayUser.profilePicture}
                      alt="Photo de profil"
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <img
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${displayUser?.username || displayUser?.id}`}
                      alt="Avatar par défaut"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={profileFileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => profileFileRef.current?.click()}
                    className="cosmic-button-secondary text-sm px-4 py-2"
                  >
                    Changer la photo
                  </button>
                  <p className="text-emerald-300/60 text-xs mt-2">
                    JPG, PNG ou GIF. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Bannière */}
            <div>
              <label className="block text-emerald-200 text-sm font-medium mb-2">Bannière</label>
              <div className="space-y-2">
                {displayUser?.bannerImage && (
                  <div className="relative h-32 rounded-lg overflow-hidden border border-emerald-700/50">
                    <img 
                      src={displayUser.bannerImage} 
                      alt="Bannière" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <input
                  ref={bannerFileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => bannerFileRef.current?.click()}
                  className="cosmic-button-secondary text-sm px-4 py-2"
                >
                  {displayUser?.bannerImage ? 'Changer la bannière' : 'Ajouter une bannière'}
                </button>
                <p className="text-emerald-300/60 text-xs">
                  Recommandé: 1500x500px. JPG ou PNG. Max 5MB.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-emerald-200 text-sm font-medium mb-2">Nom d'affichage</label>
              <input
                type="text"
                value={editForm.displayName}
                onChange={(e) => setEditForm({...editForm, displayName: e.target.value})}
                className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg p-2 sm:p-3 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70 backdrop-blur-sm text-sm sm:text-base"
                placeholder="Votre nom d'affichage"
              />
            </div>
            <div>
              <label className="block text-emerald-200 text-sm font-medium mb-2">Bio</label>
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                className="w-full bg-gray-800/70 border border-emerald-800/50 rounded-lg p-2 sm:p-3 text-emerald-100 placeholder-emerald-300/60 focus:ring-2 focus:ring-cyan-400/70 backdrop-blur-sm text-sm sm:text-base"
                rows={3}
                placeholder="Parlez-nous de vous..."
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="submit"
                disabled={profileLoading}
                className="cosmic-button disabled:opacity-50 w-full sm:w-auto text-sm lg:text-sm px-3 lg:px-4 py-2 lg:py-2"
              >
                {profileLoading ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="cosmic-button-secondary w-full sm:w-auto text-sm lg:text-sm px-3 lg:px-4 py-2 lg:py-2"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Navigation Tabs - Style X/Twitter Simplifié */}
      <div className="cosmic-card p-0 overflow-hidden">
        <div className="flex border-b border-emerald-800/30">
          {[
            { id: 'posts', label: 'Posts' },
            { id: 'likes', label: 'Likes' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 py-4 font-semibold text-sm transition-colors relative ${
                activeTab === tab.id 
                  ? 'text-cyan-300' 
                  : 'text-gray-400 hover:bg-emerald-800/10'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-400 rounded-t-full" />
              )}
            </button>
          ))}
        </div>
        
        {/* Tab Content - Mobile Responsive */}
        <div className="p-3 sm:p-4 md:p-6">
          {activeTab === 'posts' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Filtrage par catégorie */}
              <div className="flex overflow-x-auto scrollbar-hide gap-2 pb-2">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`flex-shrink-0 px-3 py-2 lg:px-4 lg:py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 cosmic-hover-effect btn-touch ${
                      selectedCategory === category.value
                        ? 'bg-gradient-to-r from-emerald-700 to-cyan-600 text-emerald-50 shadow-cosmic-neon border border-cyan-400/50'
                        : 'bg-gray-800/80 text-emerald-100 hover:bg-emerald-800/30 hover:text-cyan-300 border border-emerald-800/50'
                    }`}
                  >
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <span>{category.emoji}</span>
                      <span className="hidden sm:inline lg:inline">{category.label}</span>
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Posts filtrés */}
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">
                    {selectedCategory === 'all' ? 'Aucun post' : `Aucun post dans la catégorie ${categories.find(c => c.value === selectedCategory)?.label}`}
                  </h3>
                  <p className="text-gray-400 text-sm sm:text-base">
                    {selectedCategory === 'all' 
                      ? "Vous n'avez pas encore publié de contenu" 
                      : "Aucun contenu dans cette catégorie"
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'likes' && (
            <div className="text-center py-12">
              <p className="text-gray-400">Fonctionnalité en cours de développement</p>
            </div>
          )}
        </div>
        </div>

        {/* Notification visuelle */}
        {notification && (
          <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 bg-emerald-800 text-white rounded-xl shadow-cosmic-neon font-semibold animate-fade-in">
            {notification}
          </div>
        )}
        </div>

        {/* Right Sidebar - Style X/Twitter sticky */}
        <div className="hidden lg:block lg:w-80">
        <div className="sticky top-20 space-y-4 pb-6">
        {/* Trending Tokens cosmique */}
        <div className="cosmic-card p-4">
          <h3 className="text-base font-semibold text-emerald-100 mb-3 flex items-center">
            🔥 <span className="ml-2 neon-text">Tokens Trending</span>
          </h3>
          <div className="space-y-2">
            {trendingTokens.map((token) => (
              <TokenInfoCard 
                key={token.address}
                token={token} 
                compact={true}
              />
            ))}
          </div>
          <button 
            className="w-full mt-3 text-cyan-300 hover:text-cyan-200 text-sm font-medium transition-colors cosmic-hover-effect"
            onClick={() => navigate('/tokens')}
          >
            Voir tous les tokens →
          </button>
        </div>

        {/* Activity Feed */}
        <div className="cosmic-card">
          <RecentActivity showUserActivities={false} maxActivities={5} />
        </div>

        {/* Profile Stats cosmique */}
        {displayUser && (
          <div className="cosmic-card p-4">
            <h3 className="text-base font-semibold text-emerald-100 mb-3">📊 <span className="neon-text">Stats du Profil</span></h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Posts</span>
                <span className="text-emerald-100 font-medium">{userPosts.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Abonnés</span>
                <span className="text-emerald-100 font-medium">{followers.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Abonnements</span>
                <span className="text-emerald-100 font-medium">{following.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Niveau</span>
                <span className="text-cyan-300 font-medium neon-text">{getUserLevel().level}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">XP</span>
                <span className="text-emerald-300 font-medium">{displayUser.xp || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-emerald-200/80">Badges</span>
                <span className="text-cyan-400 font-medium">{displayUser.badges?.length || 0}</span>
              </div>
            </div>
          </div>
        )}
        </div>
        </div>      {/* Modals avec overlay cosmique */}
      <UserProfileModal 
        isOpen={isProfileModalOpen}
        userId={selectedUserId}
        onClose={handleCloseProfileModal}
        onViewFullProfile={handleViewFullProfile}
        />
        
        {/* Hidden File Inputs */}
        <input
          ref={profileFileRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePictureUpload}
          className="hidden"
        />
        <input
          ref={bannerFileRef}
          type="file"
          accept="image/*"
          onChange={handleBannerUpload}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default Profile;
